import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, BookIcon, UsersIcon, BuildingIcon, HeartHandshakeIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Library Services
{
  id: 'ls1',
  title: 'Library Membership Registration',
  description: 'Register for a new library membership or renew existing one',
  category: 'Library Services',
  estimatedTime: '1-2 business days',
  fee: 'Annual fee varies'
}, {
  id: 'ls2',
  title: 'Museum / Cultural Site Admission',
  description: 'Book tickets and tours for local museums and cultural sites',
  category: 'Library Services',
  estimatedTime: 'Same day',
  fee: 'Varies by venue'
},
// Recreation Programs
{
  id: 'rp1',
  title: 'Recreation Program Registration',
  description: 'Register for sports, arts, and fitness programs',
  category: 'Recreation Programs',
  estimatedTime: '2-3 business days',
  fee: 'Varies by program'
}, {
  id: 'rp2',
  title: 'Recreation & Culture Levies',
  description: 'Process payments for recreation and cultural program fees',
  category: 'Recreation Programs',
  estimatedTime: 'Same day',
  fee: 'As applicable'
},
// Facility Bookings
{
  id: 'fb1',
  title: 'Community Facility Booking',
  description: 'Reserve community halls, rooms, or sports fields',
  category: 'Facility Bookings',
  estimatedTime: '3-5 business days',
  fee: 'Varies by facility'
}, {
  id: 'fb2',
  title: 'Park Permit Application',
  description: 'Apply for permits for events, picnics, or special use of parks',
  category: 'Facility Bookings',
  estimatedTime: '5-7 business days',
  fee: '$50.00 - $200.00'
},
// Community Engagement
{
  id: 'ce1',
  title: 'Volunteer Application',
  description: 'Apply to volunteer for local events and programs',
  category: 'Community Engagement',
  estimatedTime: '5-7 business days',
  fee: 'No fee'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Library Services':
      return <BookIcon className="h-6 w-6 text-blue-600" data-id="element-3601" />;
    case 'Recreation Programs':
      return <UsersIcon className="h-6 w-6 text-green-600" data-id="element-3602" />;
    case 'Facility Bookings':
      return <BuildingIcon className="h-6 w-6 text-purple-600" data-id="element-3603" />;
    case 'Community Engagement':
      return <HeartHandshakeIcon className="h-6 w-6 text-orange-600" data-id="element-3604" />;
    default:
      return <BookIcon className="h-6 w-6 text-gray-600" data-id="element-3605" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3606">
      <div className="p-5 flex flex-col h-full" data-id="element-3607">
        <div className="flex-1" data-id="element-3608">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3609">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3610">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3611">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3612">
            <div className="flex items-center" data-id="element-3613">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3614">
                ⏱
              </span>
              <span data-id="element-3615">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3616">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3617">
                💰
              </span>
              <span data-id="element-3618">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3619">
            Register Now
            <span className="ml-1" data-id="element-3620">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3621">
      <div className="flex items-center mb-4" data-id="element-3622">
        <CategoryIcon category={category} data-id="element-3623" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3624">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3625">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3626" />)}
      </div>
    </div>;
};
export const CultureRecreationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3627">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3628">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3629">
          <a href="/" className="hover:text-gray-700" data-id="element-3630">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3631" />
          <span className="text-gray-900" data-id="element-3632">Culture & Recreation</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3633">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3634">
            Culture & Recreation Services
          </h1>
          <p className="text-gray-600" data-id="element-3635">
            Access community facilities, programs, and cultural services
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3636">
          <div className="max-w-md relative" data-id="element-3637">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3638">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3639" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3640" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3641" />)}
      </div>
    </div>;
};